import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { DimensionMetadataDto, MetricMetadataDto, ReportingGrainsDto } from '../../../api/generated/models';
import type { IReport } from './report.interface';

dayjs.extend(utc);

export const formatTimestampUtc = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = dayjs.utc(value as string);
  if (!parsed.isValid()) return String(value);
  return parsed.format('YYYY-MM-DD HH:mm:ss');
};

export interface IColumnFormatSpec {
  prefix?: string;
  suffix?: string;
  decimals?: number;
}

export const formatNumericDisplay = (value: unknown, spec: IColumnFormatSpec): string => {
  const actual =
    value !== null && typeof value === 'object' && 'value' in (value as object)
      ? (value as { value: unknown }).value
      : value;
  if (actual === null || actual === undefined || actual === '') return '';
  const num = typeof actual === 'number' ? actual : Number.parseFloat(String(actual));
  if (Number.isNaN(num)) return String(actual);
  const decimals = Math.min(6, Math.max(0, spec.decimals ?? 0));
  return `${spec.prefix ?? ''}${num.toFixed(decimals)}${spec.suffix ?? ''}`;
};

/**
 * Hand-maintained mirrors of the backend's sets of the same name. They drive family inference,
 * dimension gating and the cost estimate — a core metric missing here is classed as a calculated
 * metric and inferred into the wrong family, so keep them in step when the backend adds one.
 */
export const IMPRESSION_FREQUENCY_METRIC = 'impression_frequency';
export const REACH_METRIC = 'reach';

/** Both are capped server-side; the picker enforces the same bound so the run never starts. */
export const RAW_PIXEL_ONLY_MAX_RANGE_DAYS = 31;

// Its distinct count has no rollup twin, so selecting it forces a raw adte_pixel scan.
export const RAW_PIXEL_ONLY_METRICS = new Set([
  IMPRESSION_FREQUENCY_METRIC,
  REACH_METRIC,
]);

/**
 * The cap message, or null when the selection is allowed.
 *
 * Derived from the current selection rather than raised on submit, so picking the metric and
 * picking the range are the same mistake whichever order they happen in.
 */
export const rawPixelOnlyRangeError = (
  metrics: Array<{ eventType: string; label?: string }>,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null => {
  const capped = metrics
    .filter((m) => RAW_PIXEL_ONLY_METRICS.has(m.eventType))
    .map((m) => m.label || m.eventType);
  if (capped.length === 0 || !startDate || !endDate) return null;

  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  const spanDays = Math.round((end - start) / 86400000);
  if (spanDays <= RAW_PIXEL_ONLY_MAX_RANGE_DAYS) return null;
  return (
    `${capped.join(', ')} can only be reported over ` +
    `${RAW_PIXEL_ONLY_MAX_RANGE_DAYS} days or fewer — this range is ${spanDays} days.`
  );
};

// Frequency lands between 2 and 8; whole numbers would hide the differences that matter.
export const FREQUENCY_FORMAT = { decimals: 2 };

/** Reach is a count of identities — decimals on it would imply a precision it does not have. */
export const REACH_FORMAT = { decimals: 0 };

export const PIXEL_DATE_METRICS = new Set([
  'served_impression_count',
  'matched_impression_count',
  'revenue',
  'cost',
  'cpm',
  'rpm',
  IMPRESSION_FREQUENCY_METRIC,
  REACH_METRIC,
  'late_conversion_count',
  'late_matched_impression_count',
]);
export const ADVERTISER_EVENT_METRICS = new Set([
  'advertiser_event_count',
  'late_advertiser_event_count',
]);
export const LATE_METRICS = [
  'late_conversion_count',
  'late_matched_impression_count',
  'late_advertiser_event_count',
];
const EVENT_METRIC_PREFIX = 'event::';
const MATCHED_EVENT_METRIC_PREFIX = 'matched_event::';
const SUM_EVENT_METRIC_PREFIX = 'sum_event::';
export const eventMetricKey = (eventName: string): string =>
  `${EVENT_METRIC_PREFIX}${String(eventName || '')
    .trim()
    .toLowerCase()}`;
export const isEventMetricKey = (metricKey: string): boolean =>
  metricKey.startsWith(EVENT_METRIC_PREFIX);
export const matchedEventMetricKey = (eventName: string): string =>
  `${MATCHED_EVENT_METRIC_PREFIX}${String(eventName || '')
    .trim()
    .toLowerCase()}`;
export const isMatchedEventMetricKey = (metricKey: string): boolean =>
  metricKey.startsWith(MATCHED_EVENT_METRIC_PREFIX);
export const sumEventMetricKey = (eventName: string): string =>
  `${SUM_EVENT_METRIC_PREFIX}${String(eventName || '')
    .trim()
    .toLowerCase()}`;
export const isSumEventMetricKey = (metricKey: string): boolean =>
  metricKey.startsWith(SUM_EVENT_METRIC_PREFIX);
export const eventNameFromMetricKey = (metricKey: string): string =>
  isEventMetricKey(metricKey) ? metricKey.slice(EVENT_METRIC_PREFIX.length) : '';
export const eventNameFromMatchedEventMetricKey = (metricKey: string): string =>
  isMatchedEventMetricKey(metricKey) ? metricKey.slice(MATCHED_EVENT_METRIC_PREFIX.length) : '';
export const eventNameFromSumEventMetricKey = (metricKey: string): string =>
  isSumEventMetricKey(metricKey) ? metricKey.slice(SUM_EVENT_METRIC_PREFIX.length) : '';
// Predicted metrics are per event, exactly like attributed event metrics: one pickable metric per
// event rather than one abstract metric whose meaning depends on what else is selected.
const PREDICTED_METRIC_PREFIX = 'predicted::';
export const predictedMetricKey = (eventName: string): string =>
  `${PREDICTED_METRIC_PREFIX}${String(eventName || '')
    .trim()
    .toLowerCase()}`;
export const isPredictedMetricKey = (metricKey: string): boolean =>
  metricKey.startsWith(PREDICTED_METRIC_PREFIX);
/**
 * What a report of nothing but predictions is measured against. Predicted metrics are served by a
 * separate read, so on their own they ask the query for no columns at all — and a prediction with
 * nothing beside it has no scale to be read against, on the chart or in the grid. The cheapest metric
 * there is, so anchoring to it costs nothing.
 */
export const PREDICTION_ANCHOR_METRIC = 'served_impression_count';

export const eventNameFromPredictedMetricKey = (metricKey: string): string =>
  isPredictedMetricKey(metricKey) ? metricKey.slice(PREDICTED_METRIC_PREFIX.length) : '';

const predictedEventSlug = (eventName: string): string =>
  String(eventName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'unknown';

// Must match the backend's column naming exactly, including the slug, or the column arrives unlabelled.
export const predictedColumnKeyFromEventName = (eventName: string): string =>
  `predicted_${predictedEventSlug(eventName)}`;
export const predictedRemainingColumnKeyFromEventName = (eventName: string): string =>
  `predicted_remaining_${predictedEventSlug(eventName)}`;
export const predictedErrorColumnKeyFromEventName = (eventName: string): string =>
  `predicted_error_pct_${predictedEventSlug(eventName)}`;

/**
 * How much error a reader should tolerate before treating the number as directional rather than
 * usable. Business thresholds, not statistical ones: the model has no opinion on where "good" ends.
 */
export const PREDICTION_ERROR_GOOD_PCT = 15;
export const PREDICTION_ERROR_BAD_PCT = 25;

export type PredictionErrorBand = 'good' | 'warn' | 'bad';

export const predictionErrorBand = (value: unknown): PredictionErrorBand | null => {
  const pct = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  if (!Number.isFinite(pct)) return null;
  if (pct < PREDICTION_ERROR_GOOD_PCT) return 'good';
  if (pct <= PREDICTION_ERROR_BAD_PCT) return 'warn';
  return 'bad';
};

/** Read at a glance on the chart, matching the grid's colouring of the same figure. */
export const PREDICTION_BAND_COLOR: Record<PredictionErrorBand, string> = {
  good: '#4caf50',
  warn: '#ffa726',
  bad: '#ef5350',
};

export const eventColumnKeyFromEventName = (eventName: string): string => {
  const slug = String(eventName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `event_${slug || 'unknown'}`;
};
export const matchedEventColumnKeyFromEventName = (eventName: string): string => {
  const slug = String(eventName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `matched_event_${slug || 'unknown'}`;
};
export const sumEventColumnKeyFromEventName = (eventName: string): string => {
  const slug = String(eventName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `sum_event_${slug || 'unknown'}`;
};

export const getDateDimensionLabel = (
  metrics: Array<{ eventType: string }>,
  reportType: 'pixel' | 'advertiser' | undefined,
  labels: Map<string, string>
): string => {
  const isAdvertiser =
    reportType === 'advertiser' ||
    (reportType !== 'pixel' && metrics.some((m) => ADVERTISER_EVENT_METRICS.has(m.eventType)));
  const key = isAdvertiser ? 'event_date' : 'pixel_date';
  return labels.get(key) ?? key;
};

// event_date is in no rollup grain, so leaving it unmapped makes the estimate read slow.
const effectiveReportDimensions = (report: IReport): string[] => {
  const userSelectedDimensions = report.dimensions.map((d) => d.key);
  const shouldAutoAddPixelDate =
    report.timeAggregation &&
    report.timeAggregation !== 'Sum' &&
    !userSelectedDimensions.includes('pixel_date');
  const groupingDimensions = shouldAutoAddPixelDate
    ? ['pixel_date', ...userSelectedDimensions]
    : userSelectedDimensions;
  const filterDimensions = (report.filters || [])
    .filter((f) => f.dimension && f.specTarget && f.specTarget.length > 0)
    .map((f) => f.dimension);
  return [...groupingDimensions, ...filterDimensions];
};

const hasServedMetric = (metrics: Array<{ eventType: string }>): boolean => {
  const keys = new Set(metrics.map((m) => m.eventType));
  return [
    'served_impression_count',
    'revenue',
    'cost',
    'cpm',
    'rpm',
    IMPRESSION_FREQUENCY_METRIC,
    REACH_METRIC,
  ].some((m) => keys.has(m));
};

const hasRawPixelOnlyMetric = (metrics: Array<{ eventType: string }>): boolean =>
  metrics.some((m) => RAW_PIXEL_ONLY_METRICS.has(m.eventType));

const reportUsesSlowPath = (report: IReport, grains: ReportingGrainsDto): boolean => {
  if (report.reportType === 'advertiser') return true;

  const metricKeys = new Set(report.metrics.map((m) => m.eventType));
  const hasMatchedImpression = metricKeys.has('matched_impression_count');
  const hasAttributedEvent = report.metrics.some(
    (m) => isEventMetricKey(m.eventType) || isSumEventMetricKey(m.eventType)
  );
  const hasMatchedEvent = report.metrics.some((m) => isMatchedEventMetricKey(m.eventType));
  // Late metrics derive their rows from a raw impression scan at query time, so they are never
  // on a fast path however cheap the dimensions look.
  const hasLateMetric = LATE_METRICS.some((m) => metricKeys.has(m));

  // A calc metric compiles against the raw/detail scan and has no aggregate, so any of
  // them forces a slow path even when the dimensions are fast.
  const hasCalcMetric = report.metrics.some((m) => {
    const k = m.eventType;
    return (
      !PIXEL_DATE_METRICS.has(k) &&
      !ADVERTISER_EVENT_METRICS.has(k) &&
      !isEventMetricKey(k) &&
      !isMatchedEventMetricKey(k) &&
      !isSumEventMetricKey(k)
    );
  });

  if (
    hasMatchedImpression ||
    hasMatchedEvent ||
    hasCalcMetric ||
    hasLateMetric ||
    hasRawPixelOnlyMetric(report.metrics)
  )
    return true;

  const dims = effectiveReportDimensions(report);
  const servedRollup = new Set(grains.servedRollup);
  const preaggregated = new Set(grains.preaggregated);
  if (hasServedMetric(report.metrics) && !dims.every((d) => servedRollup.has(d))) return true;
  if (hasAttributedEvent && !dims.every((d) => preaggregated.has(d))) return true;
  return false;
};

const reportUsesRawPixelServedScan = (
  report: IReport,
  grains: ReportingGrainsDto
): boolean => {
  if (report.reportType === 'advertiser') return false;
  if (!hasServedMetric(report.metrics)) return false;
  if (hasRawPixelOnlyMetric(report.metrics)) return true;
  const servedRollup = new Set(grains.servedRollup);
  return !effectiveReportDimensions(report).every((d) => servedRollup.has(d));
};

/** Only rawPixel is measured; the rest are ranked guesses. */
const SOURCE_SECONDS_PER_DAY = {
  // Measured at ~0.5-0.7 once the pixel scan went parallel; kept at 1 so a cold cache still fits.
  rawPixel: 1,
  detailAttribution: 2,
  rawConversion: 1,
  aggregate: 0.5,
} as const;

/** Both dates fan out across the attribution window; 4x fitted to one 120s-vs-35s run. */
const CROSS_AXIS_DATE_FACTOR = 4;

const crossAxisDateFactor = (report: IReport): number => {
  const keys = new Set(report.dimensions.map((d) => d.key));
  return keys.has('pixel_date') && keys.has('event_date') ? CROSS_AXIS_DATE_FACTOR : 1;
};

const dominantSourceCost = (report: IReport, grains: ReportingGrainsDto): number => {
  if (reportUsesRawPixelServedScan(report, grains)) return SOURCE_SECONDS_PER_DAY.rawPixel;
  const metricKeys = new Set(report.metrics.map((m) => m.eventType));

  const needsDetail =
    metricKeys.has('matched_impression_count') ||
    report.metrics.some((m) => isMatchedEventMetricKey(m.eventType)) ||
    report.metrics.some(
      (m) =>
        !PIXEL_DATE_METRICS.has(m.eventType) &&
        !ADVERTISER_EVENT_METRICS.has(m.eventType) &&
        !isEventMetricKey(m.eventType) &&
        !isMatchedEventMetricKey(m.eventType) &&
        !isSumEventMetricKey(m.eventType)
    );
  if (needsDetail) return SOURCE_SECONDS_PER_DAY.detailAttribution;
  if (report.reportType === 'advertiser') return SOURCE_SECONDS_PER_DAY.rawConversion;
  return SOURCE_SECONDS_PER_DAY.aggregate;
};

const reportRangeDays = (report: IReport): number => {
  if (!report.startDate || !report.endDate) return 0;
  const start = dayjs(report.startDate);
  const end = dayjs(report.endDate);
  if (!start.isValid() || !end.isValid()) return 0;
  return Math.abs(end.diff(start, 'day'));
};

export const fastDimensionKeys = (report: IReport, grains: ReportingGrainsDto): string[] => {
  if (report.reportType === 'advertiser') return [];
  if (hasRawPixelOnlyMetric(report.metrics)) return [];
  const keys = hasServedMetric(report.metrics) ? grains.servedRollup : grains.preaggregated;
  return keys.filter((k) => k !== 'pixel_date' && k !== 'event_date' && k !== 'event');
};

export interface ReportSpeedEstimate {
  level: 'fast' | 'moderate' | 'slow';
  label: string;
  estimate: string;

  score: number;
}

const FIRST_RUN_STATEMENT_FACTOR = 1.6;

export const estimateReportSpeed = (
  report: IReport,
  grains: ReportingGrainsDto,
  metricCatalogue: MetricMetadataDto[]
): ReportSpeedEstimate => {
  if (!reportUsesSlowPath(report, grains)) {
    return { level: 'fast', label: 'Fast', estimate: '≈ seconds', score: 0 };
  }
  const days = Math.max(1, reportRangeDays(report) + 1);
  const extraDimensions = Math.max(0, report.dimensions.length - 1);
  const cheapKeys = new Set(metricCatalogue.filter((m) => m.cheap).map((m) => m.key));
  const expensiveMetrics = report.metrics.filter((m) => !cheapKeys.has(m.eventType)).length;

  const seconds =
    dominantSourceCost(report, grains) *
    days *
    FIRST_RUN_STATEMENT_FACTOR *
    (1 + 0.25 * extraDimensions) *
    (1 + 0.05 * expensiveMetrics) *
    crossAxisDateFactor(report);

  if (seconds < 20) {
    return { level: 'moderate', label: 'Moderate', estimate: '≈ under 30 seconds', score: 0.2 };
  }
  if (seconds < 50) {
    return { level: 'moderate', label: 'Moderate', estimate: '≈ under a minute', score: 0.35 };
  }
  if (seconds < 200) {
    return { level: 'slow', label: 'Slow', estimate: '≈ 1–3 minutes', score: 0.65 };
  }
  return { level: 'slow', label: 'Slow', estimate: '≈ 3 minutes or more', score: 0.9 };
};

export type ReportType = 'impression' | 'conversion';

const familyForMetrics = (metrics: Array<{ eventType: string }>): ReportType =>
  metrics.some((m) => ADVERTISER_EVENT_METRICS.has(m.eventType)) ? 'conversion' : 'impression';

export const getDimensionOptionsForMetrics = (
  metrics: Array<{ eventType: string }>,
  catalogue: DimensionMetadataDto[]
): Array<{ key: string; value: string }> => {
  const family = familyForMetrics(metrics);
  const hasServed = metrics.some((m) => m.eventType === 'served_impression_count');
  return catalogue
    .filter((d) => (d.reports ?? []).includes(family))
    .filter((d) => !hasServed || !d.blockedWithServed)
    .map((d) => ({ key: d.key, value: d.label }));
};

export const getFilterDimensionOptionsForMetrics = (
  metrics: Array<{ eventType: string }>,
  dimensions: Array<{ key: string }>,
  dimensionOptions: Array<{ key: string; value: string }>,
  catalogue: DimensionMetadataDto[]
): Array<{ key: string; value: string }> => {
  const byKey = new Map(catalogue.map((d) => [d.key, d]));
  const selectedMetrics = new Set(metrics.map((m) => m.eventType));
  const hasServed = selectedMetrics.has('served_impression_count');
  const hasMatched = selectedMetrics.has('matched_impression_count');
  const hasSyntheticBucketDimension = dimensions.some((d) => byKey.get(d.key)?.syntheticBucket);

  const nonDateOptions = dimensionOptions.filter((o) => {
    const meta = byKey.get(o.key);
    return meta?.type !== 'date' && !meta?.isTimestamp;
  });
  if (familyForMetrics(metrics) === 'conversion') return nonDateOptions;

  return nonDateOptions
    .filter((o) => !hasServed || !byKey.get(o.key)?.blockedWithServed)
    .filter((o) => {
      if (!byKey.get(o.key)?.syntheticBucket) return true;
      if (!hasServed) return true;
      return hasMatched && hasSyntheticBucketDimension;
    });
};

export type Campaign = string;

export const timeOptions = ['Day', 'Month', 'Sum']; // 'Hour',
export const timePresets = [
  'choose time preset',
  'Today',
  'Yesterday',
  'Last 7 days',
  'Last 30 days',
  'last calender month',
  'last calender week',
];

export const getTimeFromAggregationPresets = (
  preset: string
): { startDate: string | null; endDate: string | null } => {
  let startDate: Date | null = null;
  let endDate: Date | null = null;

  switch (preset) {
    case 'Today':
      startDate = new Date();
      endDate = new Date();
      break;
    case 'Yesterday':
      startDate = dayjs().subtract(1, 'day').toDate();
      endDate = dayjs().subtract(1, 'day').toDate();
      break;
    case 'Last 7 days':
      startDate = dayjs().subtract(7, 'day').toDate();
      endDate = new Date();
      break;
    case 'Last 30 days':
      startDate = dayjs().subtract(30, 'day').toDate();
      endDate = new Date();
      break;
    case 'last calender month':
      startDate = dayjs().subtract(1, 'month').startOf('month').toDate();
      endDate = dayjs().subtract(1, 'month').endOf('month').toDate();
      break;
    case 'last calender week':
      startDate = dayjs().subtract(1, 'week').startOf('week').toDate();
      endDate = dayjs().subtract(1, 'week').endOf('week').toDate();
      break;
    default:
      return { startDate: null, endDate: null };
  }

  return {
    startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
    endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
  };
};
